import React from "react";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { updateMatch } from "@/utils/match/operations/updateMatch";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";

export function MatchCustomFieldsTab({ match, refetchMatch }: { match: any, refetchMatch?: () => Promise<void> }) {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const [fields, setFields] = React.useState<CustomField[]>([]);
  const [editValues, setEditValues] = React.useState<Record<string, any>>({});
  const [originalValues, setOriginalValues] = React.useState<Record<string, any>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        const defs = data.Matches || [];
        setFields(defs);
      })
      .catch(() => {
        setError("Failed to load custom fields");
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  const handleEdit = () => {
    const merged: Record<string, any> = {};
    fields.forEach(field => {
      merged[field.name] = match.custom_fields && match.custom_fields[field.name] !== undefined
        ? match.custom_fields[field.name]
        : "";
    });
    setEditValues(merged);
    setOriginalValues(merged);
    setEditMode(true);
  };
  const handleCancel = () => {
    setEditValues(originalValues);
    setEditMode(false);
  };
  const handleChange = (name: string, value: any) => {
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleSave = async () => {
    setSaving(true);
    // Immediately close edit mode and show success toast for better UX
    setEditMode(false);
    toast({ title: "Custom fields saved!", description: "Your changes have been saved.", variant: "default" });
    
    try {
      await updateMatch(match.id, {}, editValues);
      // Refetch in background to ensure data consistency
      if (refetchMatch) {
        refetchMatch().catch(error => {
          console.error("Background refetch failed:", error);
        });
      }
    } catch (err) {
      // If save failed, revert to edit mode
      setEditMode(true);
      toast({ title: "Failed to save custom fields", description: "Could not save to server.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return <div className="p-4 text-red-600">No organization selected.</div>;
  }
  if (loading) {
    return <div className="p-4">Loading custom fields...</div>;
  }
  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }
  if (!fields.length) {
    return <div className="p-4 text-gray-500 dark:text-gray-400">{t("matches.customFields.noFields")}</div>;
  }
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold mb-1">{t("matches.customFields.title")}</h2>
        <div className="flex gap-2">
          {!editMode ? (
            <Button size="sm" variant="outline" onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" /> {t("matches.customFields.edit")}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="default" onClick={handleSave} disabled={saving}>
                <Check className="mr-2 h-4 w-4" /> {saving ? t("matches.customFields.saving") : t("matches.customFields.save")}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel} disabled={saving}>
                <X className="mr-2 h-4 w-4" /> {t("matches.customFields.cancel")}
              </Button>
            </>
          )}
        </div>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-4">{t("matches.customFields.description")}</p>
      <form className="space-y-6 pt-2">
        {fields.map((field) => {
          const value = editMode
            ? editValues[field.name] ?? ""
            : (match.custom_fields && match.custom_fields[field.name] !== undefined
                ? match.custom_fields[field.name]
                : "");
          return (
            <div key={field.name} className="flex flex-col gap-1">
              <label className="font-medium text-sm mb-1">{field.name}</label>
              {field.type === "text" && (
                <Input
                  value={value}
                  onChange={e => handleChange(field.name, e.target.value)}
                  placeholder={field.name}
                  readOnly={!editMode}
                  aria-label={field.name}
                />
              )}
              {field.type === "number" && (
                <Input
                  type="number"
                  value={value}
                  onChange={e => handleChange(field.name, e.target.value)}
                  placeholder={field.name}
                  readOnly={!editMode}
                  aria-label={field.name}
                />
              )}
              {field.type === "boolean" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!value}
                    onCheckedChange={checked => editMode && handleChange(field.name, checked)}
                    id={field.name}
                    disabled={!editMode}
                    aria-label={field.name}
                  />
                  <label htmlFor={field.name} className="text-sm">{field.name}</label>
                </div>
              )}
              {field.type === "dropdown" && field.options && (
                <Select
                  value={value}
                  onValueChange={val => editMode && handleChange(field.name, val)}
                  disabled={!editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("matches.customFields.selectOption", { field: field.name })} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </form>
    </>
  );
} 